import React from 'react';

/**
 * Props for the Controls component
 */
export interface ControlsProps {
  /** Current scale */
  scale?: number;
  /** Scale change handler */
  onScaleChange?: (scale: number) => void;
  /** Zoom in function */
  onZoomIn?: () => void;
  /** Zoom out function */
  onZoomOut?: () => void;
  /** Reset transform function */
  onReset?: () => void;
  /** Whether add mode is active */
  isAddMode?: boolean;
  /** Toggle add mode function */
  onToggleAddMode?: () => void;
  /** Whether reference line update mode is active */
  isReferenceLineUpdateMode?: boolean;
  /** Toggle reference line update mode */
  onToggleReferenceLineUpdateMode?: () => void;
  /** Export/Download function */
  onExport?: () => void;
  /** Optional class name */
  className?: string;
  /** Optional style */
  style?: React.CSSProperties;
}

/**
 * Default controls component for PoleCanvas
 */
export const Controls: React.FC<ControlsProps> = ({
  scale,
  onScaleChange,
  onZoomIn,
  onZoomOut,
  onReset,
  isAddMode = false,
  onToggleAddMode,
  isReferenceLineUpdateMode = false,
  onToggleReferenceLineUpdateMode,
  onExport,
  className = '',
  style,
}) => {
  return (
    <div
      className={`pole-canvas-controls ${className}`}
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        ...style,
      }}
    >
      {onToggleReferenceLineUpdateMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleReferenceLineUpdateMode();
          }}
          disabled={isAddMode}
          style={{
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: 4,
            backgroundColor: isReferenceLineUpdateMode ? '#fbbf24' : '#fff',
            color: isReferenceLineUpdateMode ? '#000' : '#000',
            cursor: isAddMode ? 'not-allowed' : 'pointer',
            opacity: isAddMode ? 0.5 : 1,
          }}
        >
          {isReferenceLineUpdateMode ? 'Done Updating' : 'Update Pole Top and Bottom'}
        </button>
      )}
      
      {onToggleAddMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleAddMode();
          }}
          disabled={isReferenceLineUpdateMode}
          style={{
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: 4,
            backgroundColor: isAddMode ? '#007bff' : '#fff',
            color: isAddMode ? '#fff' : '#000',
            cursor: isReferenceLineUpdateMode ? 'not-allowed' : 'pointer',
            opacity: isReferenceLineUpdateMode ? 0.5 : 1,
          }}
        >
          {isAddMode ? 'Stop Adding' : 'Add Attachments'}
        </button>
      )}
      
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onZoomIn?.();
          }}
          style={{
            width: 32,
            height: 32,
            border: '1px solid #ccc',
            borderRadius: 4,
            backgroundColor: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          +
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onZoomOut?.();
          }}
          style={{
            width: 32,
            height: 32,
            border: '1px solid #ccc',
            borderRadius: 4,
            backgroundColor: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          -
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReset?.();
          }}
          style={{
            padding: '6px 8px',
            border: '1px solid #ccc',
            borderRadius: 4,
            backgroundColor: '#fff',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          Reset
        </button>
      </div>
      
      {onExport && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onExport?.();
          }}
        style={{
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: 4,
          backgroundColor: '#28a745',
          color: '#fff',
          cursor: 'pointer',
        }}
        >
          Download
        </button>
      )}
    </div>
  );
};